import { useEffect, useState } from "react";
import { getItemWebStorage } from "../../../../features/webStorageSaver";
import { useAppSelector } from "../../../../stateManagement/hooks/redux";
import { getBasicTablePaginator } from "../../../../widgets/table/tableFeatures/tableTemplates/tablePaginatorTemplates";
import { configureTableLazyState, defineTableColumnsFromArray, defineTableFilters, TableEvents, tableStateModifyHandle } from "../../../../widgets/table/tableFeatures/tableFunctions";
import ContentHeader from "../../../../widgets/contentHeader/ContentHeader";
import LoaderWrapper from "../../../../widgets/loaderWrapper/LoaderWrapper";
import TableInfoWidget from "../../../../widgets/table/tableAddons/tableInfoWidget/TableInfoWidget";
import { exportTypes, TableExportComponent } from "../../../../widgets/tablesShared/tableTools/tableExportComponent";
import TableWidget from "../../../../widgets/table/tableWidget/TableWidget";
import { getValueWithMapLinks, getValueWithObjectCardLinks, prepareTableData } from "../../../../features/dataFormatFunctions/dataFormatter";
import { prepareQueryArgsForTableFetch } from "../../../../widgets/table/tableFeatures/tableFetchFunctions";
import { useLazyFetchRoadDiagnosticsSegmentsQuery } from "../../../../api/rtk/base/slices/diagnosticsApiSlice";
import { getMapLinkColumn, getObjectLinkColumn } from "../../../../widgets/table/tableFeatures/tableTemplates/tableColumnTemplates";
import { DataTableSelectEvent } from "primereact/datatable";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCurrentRoadDiagnosticsSegmentNew, setTableCaption, setTableDataCount, showToastMessage } from "../../../../stateManagement/reducers";
import ToastMessageContainer from "../../../../widgets/toastMessageContainer/ToastMessageContainer";
import { getDataArrayWithMaintenanceEnding, getDataArrayWithMaintenancePosition } from "../../../../features/dataFormatFunctions/formatMaintenancePoint";

const columnsStructure: any = [
    {
        headerName: "Начало, км",
        field: "Position",
        align: "left",
        valueType: "float"
    },
    {
        headerName: "Км+",
        field: "MaintenancePosition",
        valueType: "int",
        canBeFiltered: false,
        align: "left"
    },
    {
        headerName: "Конец, км",
        field: "Ending",
        valueType: "float",
        filterPlaceholder: "Конец, км  =",
        align: "left"
    },
    {
        headerName: "Км+",
        field: "MaintenanceEnding",
        valueType: "int",
        canBeFiltered: false,
        align: "left"
    },
    {
        headerName: "Тип события",
        field: "EventTypeString",
        valueType: "string",
        align: "left"
    },
    {
        headerName: "Дата начала",
        field: "StartDate",
        valueType: "date",
        align: "left"
    },
    {
        headerName: "Дата окончания",
        field: "EndDate",
        valueType: "date",
        align: "left"
    },
    {
        headerName: "Год",
        field: "EventYear",
        valueType: "float",
        align: "left"
    },
    {
        headerName: "Исполнитель",
        field: "ExecutorOrgName",
        valueType: "string",
        align: "left"
    },
    getMapLinkColumn(),
    getObjectLinkColumn()
]

const possibleEventTypeIds: any = [1, 1301]
// Диагностика - 1
// Мониторинг - 1301

export default function TableRoadDiagnosticsSegments() {
    const objectTypeName = "RoadDiagnosticsSegments"
    const tableValueUrl = "objects/RoadDiagnosticsSegments"
    const caption = "Участки диагностики"
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [columns, setColumns] = useState<any>(null)
    const [tableState, setTableState] = useState<any>(null)
    const [paginator, setPaginator] = useState<any>({})
    const [paginatorPageInputTooltip, setPaginatorPageInputTooltip] = useState<any>('Введите номер страницы.')
    const [tableValue, setTableValue] = useState<any>([])
    const [tableLoading, setTableLoading] = useState<boolean>(true)
    const { pathId } = useAppSelector(state => state.pathsInfo.selectedPath) ?? {}
    const tableInfoWindowIsShown = useAppSelector(state => state.contentHeader.tableInfoWindowIsShown)
    const dataCount = useAppSelector(state => state.table.dataCount)

    const [fetchRoadDiagnosticsSegments, { isError: isErrorTableValue }] = useLazyFetchRoadDiagnosticsSegmentsQuery()

    useEffect(() => {
        const paginatorTemplate = getBasicTablePaginator(paginatorPageInputTooltip, setPaginatorPageInputTooltip, tableState, setTableState, dataCount, objectTypeName)
        setPaginator(paginatorTemplate)
    }, [dataCount, tableState, paginatorPageInputTooltip])

    useEffect(() => {
        dispatch(setTableCaption(caption))
        let columns = defineTableColumnsFromArray(columnsStructure)
        let filters = defineTableFilters(columns)
        setColumns(columns)
        setTableState(getItemWebStorage("tablesState")?.[objectTypeName] ?? configureTableLazyState({ filters }))
    }, [])

    async function getTableValue() {
        setTableLoading(true)
        let { sortString, filterStringArray, page }: any = prepareQueryArgsForTableFetch(tableState, columns)
        filterStringArray.push(`pathId eq ${pathId}`)
        const eventTypeIdFilterString = `(${possibleEventTypeIds.map((eventTypeId: number) => `EventTypeId eq ${eventTypeId}`).join(" or ")})`
        filterStringArray.push(eventTypeIdFilterString)
        const { data: diagnosticsSegments } = await fetchRoadDiagnosticsSegments({ pages: true, top: 100, order: sortString, filterStringArray, skip: page * 100 })
        let value = diagnosticsSegments?.value
        value = getValueWithMapLinks(value, objectTypeName)
        value = getValueWithObjectCardLinks(value, objectTypeName)
        value = prepareTableData(value, columns)
        value = getDataArrayWithMaintenanceEnding(value)
        value = getDataArrayWithMaintenancePosition(value)
        setTableValue(value)
        let dataCount = diagnosticsSegments?.['@xdata.count']
        dispatch(setTableDataCount(dataCount))
        setTableLoading(false)
    }

    useEffect(() => {
        if (columns) {
            getTableValue()
        }
    }, [columns, tableState])

    const onSort = (state: any) => tableStateModifyHandle(state, TableEvents.sort, setTableState, objectTypeName)
    const onPage = (state: any) => tableStateModifyHandle(state, TableEvents.page, setTableState, objectTypeName)
    const onFilter = (state: any) => tableStateModifyHandle(state, TableEvents.filter, setTableState, objectTypeName)

    useEffect(() => {
        if (isErrorTableValue) {
            setTableLoading(false)
        }
    }, [isErrorTableValue])

    function onRowSelect(event: DataTableSelectEvent) {
        dispatch(setCurrentRoadDiagnosticsSegmentNew(event?.data))
        navigate(`${event?.data.EventID}/composition`);
    }

    useEffect(() => {
        if (isErrorTableValue) {
            dispatch(showToastMessage({ messageText: "Ошибка загрузки таблицы", messageType: "error" }))
        }
    }, [isErrorTableValue])

    return (
        <>
            <ContentHeader title="Таблица «Участки диагностики»" type="table" option="diagnosticsSegments" />
            <div className="table-widget-with-toast with-content-header">
                <ToastMessageContainer />
                <LoaderWrapper isLoading={tableLoading} >
                    {tableInfoWindowIsShown && <TableInfoWidget />}
                    {exportTypes.map((exportType: any) =>
                        <TableExportComponent
                            dataCount={dataCount}
                            tableState={tableState}
                            columns={columns}
                            objectType={objectTypeName}
                            url={tableValueUrl}
                            pathId={pathId}
                            exportType={exportType}
                            exportName="Участки диагностики"
                        />
                    )}
                    <TableWidget
                        tableValue={tableValue}
                        tableState={tableState}
                        columns={columns}
                        onRowSelect={onRowSelect}
                        dataCount={dataCount}
                        paginatorTemplate={paginator}
                        pageSize={100}
                        onPage={onPage}
                        onSort={onSort}
                        onFilter={onFilter}
                        isLazy={true}
                    />
                </LoaderWrapper>
            </div >
        </>
    )
}

